import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { runsAPI } from '../utils/api';
import { useWebSocket } from '../hooks/useWebSocket';
import StatusBadge from '../components/StatusBadge';
import ProjectSteps from '../components/ProjectSteps';
import LogViewer from '../components/LogViewer';
import FileBrowser from '../components/FileBrowser';
import GitHistory from '../components/GitHistory';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Clock,
  DollarSign,
  Layers,
  Loader2,
  XCircle,
  RotateCcw,
  CheckCircle2,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Charger les données initiales
  useEffect(() => {
    loadRun();
  }, [id]);

  const loadRun = async () => {
    try {
      const res = await runsAPI.get(id);
      const data = res.data;
      setRun(data);
      setSteps(data.steps || []);
      setLogs(data.logs || []);
    } catch (err) {
      toast.error('Impossible de charger le projet');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  // WebSocket pour les mises à jour en temps réel
  const onLog = useCallback((data) => {
    setLogs((prev) => [...prev, data]);
  }, []);

  const onStepUpdate = useCallback((data) => {
    setSteps((prev) => {
      const idx = prev.findIndex(
        (s) => s.step_number === data.step_number || s.id === data.id
      );
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...data };
        return updated;
      }
      return [...prev, data];
    });
    if (data.step_number !== undefined) {
      setRun((prev) => prev ? { ...prev, current_step: data.step_number } : prev);
    }
  }, []);

  const onStatusUpdate = useCallback((data) => {
    setRun((prev) => prev ? { ...prev, status: data.status } : prev);
    if (data.status === 'completed') {
      toast.success('Projet terminé !');
    } else if (data.status === 'failed') {
      toast.error('Le projet a échoué');
    }
  }, []);

  const onValidation = useCallback(() => {
    setRun((prev) => prev ? { ...prev, status: 'awaiting_validation' } : prev);
    toast.info('Validation requise', { duration: 10000 });
  }, []);

  const { connected } = useWebSocket(id, {
    onLog,
    onStepUpdate,
    onStatusUpdate,
    onValidation,
  });

  // Actions
  const handleCancel = async () => {
    try {
      await runsAPI.cancel(id);
      toast.info('Annulation demandée');
      loadRun();
    } catch {
      toast.error("Impossible d'annuler");
    }
  };

  const handleValidatePlan = async (approved) => {
    try {
      await runsAPI.validatePlan(id, approved, '');
      toast.success(approved ? 'Plan approuvé' : 'Plan rejeté');
      loadRun();
    } catch {
      toast.error('Erreur de validation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!run) return null;

  const isRunning = run.status === 'running';
  const isAwaitingValidation = run.status === 'awaiting_validation';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/projects')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <StatusBadge status={run.status} />
            {connected && isRunning && (
              <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-400/30">
                En direct
              </Badge>
            )}
          </div>
          <h2 className="text-lg font-semibold text-foreground line-clamp-2">
            {run.goal}
          </h2>

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Badge variant="outline" className="text-xs capitalize">{run.stack}</Badge>
            </span>
            <span className="flex items-center gap-1">
              <Layers className="w-3 h-3" />
              Étape {run.current_step || 0}/{run.max_steps || 20}
            </span>
            {run.cost_used_eur > 0 && (
              <span className="flex items-center gap-1">
                <DollarSign className="w-3 h-3" />
                {run.cost_used_eur.toFixed(4)} EUR
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {run.created_at
                ? formatDistanceToNow(new Date(run.created_at), { addSuffix: true, locale: fr })
                : '-'}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {isAwaitingValidation && (
            <>
              <Button size="sm" variant="destructive" onClick={() => handleValidatePlan(false)}>
                <XCircle className="w-4 h-4 mr-1" />
                Rejeter
              </Button>
              <Button size="sm" onClick={() => handleValidatePlan(true)} className="glow-primary">
                <CheckCircle2 className="w-4 h-4 mr-1" />
                Approuver
              </Button>
            </>
          )}
          {isRunning && (
            <Button size="sm" variant="destructive" onClick={handleCancel}>
              <XCircle className="w-4 h-4 mr-1" />
              Annuler
            </Button>
          )}
          {(run.status === 'failed' || run.status === 'cancelled') && (
            <Button size="sm" variant="outline" onClick={() => navigate('/projects/new')}>
              <RotateCcw className="w-4 h-4 mr-1" />
              Relancer
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="steps" className="w-full">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="steps">
            Étapes ({steps.length})
          </TabsTrigger>
          <TabsTrigger value="files">Fichiers</TabsTrigger>
          <TabsTrigger value="logs">
            Logs ({logs.length})
          </TabsTrigger>
          <TabsTrigger value="git">Git</TabsTrigger>
        </TabsList>

        <TabsContent value="steps" className="mt-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <ProjectSteps
                steps={steps}
                currentStep={run.current_step}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="files" className="mt-4">
          <FileBrowser projectId={id} />
        </TabsContent>

        <TabsContent value="logs" className="mt-4">
          <Card className="bg-card border-border overflow-hidden">
            <LogViewer logs={logs} />
          </Card>
        </TabsContent>

        <TabsContent value="git" className="mt-4">
          <Card className="bg-card border-border">
            <GitHistory projectId={id} />
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
